import { OjWebElement } from '@oracle/oraclejet-webdriver/elements';

/**
 * This is the base class for oj-c-picto-chart WebElement, and is generated from the
 * component's metadata. Do not modify these contents since they'll be replaced
 * during the next generation.
 * Put overrides into the WebElements's subclass, PictoChartWebElement.ts.
 */
export class PictoChartWebElementBase extends OjWebElement {
  /**
   * Gets the value of <code>data</code> property.
   * Specifies the DataProvider for the sections and items of the picto-chart
   * @return The value of <code>data</code> property.
   *
   */
  public getData(): Promise<null> {
    return this.getProperty<null>('data');
  }

  /**
   * Sets the value of <code>hiddenCategories</code> property.
   * An array of categories that will be hidden.
   * @param hiddenCategories The value to set for <code>hiddenCategories</code>
   *
   */
  public changeHiddenCategories(hiddenCategories: Array<string>): Promise<void> {
    return this.setProperty<Array<string>>('hiddenCategories', hiddenCategories);
  }

  /**
   * Gets the value of <code>hiddenCategories</code> property.
   * An array of categories that will be hidden.
   * @return The value of <code>hiddenCategories</code> property.
   *
   */
  public getHiddenCategories(): Promise<Array<string>> {
    return this.getProperty<Array<string>>('hiddenCategories');
  }

  /**
   * Gets the value of <code>highlightMatch</code> property.
   * The matching condition for the highlightedCategories option. By default, highlightMatch is 'all' and only items whose categories match all of the values specified in the highlightedCategories array will be highlighted. If highlightMatch is 'any', then items that match at least one of the highlightedCategories values will be highlighted.
   * @return The value of <code>highlightMatch</code> property.
   *
   */
  public getHighlightMatch(): Promise<string> {
    return this.getProperty<string>('highlightMatch');
  }

  /**
   * Sets the value of <code>highlightedCategories</code> property.
   * An array of categories that will be highlighted.
   * @param highlightedCategories The value to set for <code>highlightedCategories</code>
   *
   */
  public changeHighlightedCategories(highlightedCategories: Array<string>): Promise<void> {
    return this.setProperty<Array<string>>('highlightedCategories', highlightedCategories);
  }

  /**
   * Gets the value of <code>highlightedCategories</code> property.
   * An array of categories that will be highlighted.
   * @return The value of <code>highlightedCategories</code> property.
   *
   */
  public getHighlightedCategories(): Promise<Array<string>> {
    return this.getProperty<Array<string>>('highlightedCategories');
  }

  /**
   * Gets the value of <code>hoverBehavior</code> property.
   * Defines the behavior applied when hovering over data items.
   * @return The value of <code>hoverBehavior</code> property.
   *
   */
  public getHoverBehavior(): Promise<string> {
    return this.getProperty<string>('hoverBehavior');
  }

  /**
   * Gets the value of <code>layout</code> property.
   * The direction in which the items are laid out.
   * @return The value of <code>layout</code> property.
   *
   */
  public getLayout(): Promise<string> {
    return this.getProperty<string>('layout');
  }

  /**
   * Gets the value of <code>drilling</code> property.
   * Drillable items will show a pointer cursor on hover and fire an ojDrill event on click (double click if selection is enabled).
   * @return The value of <code>drilling</code> property.
   *
   */
  public getDrilling(): Promise<string> {
    return this.getProperty<string>('drilling');
  }

  /**
   * Gets the value of <code>layoutOrigin</code> property.
   * Defines where the first item is rendered. The subsequent items follow the first item according to the layout.
   * @return The value of <code>layoutOrigin</code> property.
   *
   */
  public getLayoutOrigin(): Promise<string> {
    return this.getProperty<string>('layoutOrigin');
  }

  /**
   * Gets the value of <code>columnCount</code> property.
   * Specifies the column count.
   * @return The value of <code>columnCount</code> property.
   *
   */
  public getColumnCount(): Promise<number> {
    return this.getProperty<number>('columnCount');
  }

  /**
   * Gets the value of <code>columnWidth</code> property.
   * Specifies the column width.
   * @return The value of <code>columnWidth</code> property.
   *
   */
  public getColumnWidth(): Promise<number | string> {
    return this.getProperty<number | string>('columnWidth');
  }

  /**
   * Gets the value of <code>contextMenuConfig</code> property.
   * Specifies a context menu configuration.
   * @return The value of <code>contextMenuConfig</code> property.
   *
   */
  public getContextMenuConfig(): Promise<ContextMenuConfig> {
    return this.getProperty<ContextMenuConfig>('contextMenuConfig');
  }

  /**
   * Gets the value of <code>rowCount</code> property.
   * Specifies the row count.
   * @return The value of <code>rowCount</code> property.
   *
   */
  public getRowCount(): Promise<number> {
    return this.getProperty<number>('rowCount');
  }

  /**
   * Gets the value of <code>rowHeight</code> property.
   * Specifies the row height.
   * @return The value of <code>rowHeight</code> property.
   *
   */
  public getRowHeight(): Promise<number | string> {
    return this.getProperty<number | string>('rowHeight');
  }

  /**
   * Gets the value of <code>selectionMode</code> property.
   * Specifies the selection mode.
   * @return The value of <code>selectionMode</code> property.
   *
   */
  public getSelectionMode(): Promise<string> {
    return this.getProperty<string>('selectionMode');
  }

  /**
   * Sets the value of <code>selection</code> property.
   * An array of id strings, used to define the selected objects.
   * @param selection The value to set for <code>selection</code>
   *
   */
  public changeSelection(selection: Array<string | number>): Promise<void> {
    return this.setProperty<Array<string | number>>('selection', selection);
  }

  /**
   * Gets the value of <code>selection</code> property.
   * An array of id strings, used to define the selected objects.
   * @return The value of <code>selection</code> property.
   *
   */
  public getSelection(): Promise<Array<string | number>> {
    return this.getProperty<Array<string | number>>('selection');
  }
}
export interface ContextMenuConfig {
  /**
   *
   */
  accessibleLabel: string;
}
